import React from 'react';
import { MousePointer2, Type, Image, Square, Menu, ChevronRight } from 'lucide-react';
import { useTheme } from '@/contexts/ThemeContext';

interface FunnelBuilderContentProps {
  animationDelay?: number;
}

const pageSections = [
  { name: 'Navigation Menu', type: 'nav', active: false },
  { name: 'Hero Section', type: 'hero', active: true },
  { name: 'Features Grid', type: 'features', active: false },
  { name: 'CTA Banner', type: 'cta', active: false },
  { name: 'Footer', type: 'footer', active: false },
];

const FunnelBuilderContent: React.FC<FunnelBuilderContentProps> = ({ animationDelay = 0 }) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';

  return (
    <div 
      className="flex-1 flex overflow-hidden"
      style={{ 
        backgroundColor: isDark ? '#1A1A1A' : '#F5F5F5',
        animation: `fadeIn 0.4s ease-out ${animationDelay}ms both`
      }}
    >
      {/* Left Sidebar - Page Sections */}
      <div 
        className="w-28 border-r flex flex-col"
        style={{ 
          borderColor: isDark ? '#333333' : '#E5E5E5',
          backgroundColor: isDark ? '#222222' : '#FFFFFF',
          animation: `slideInFromLeft 0.4s ease-out ${animationDelay + 100}ms both`
        }}
      >
        <div className="p-2 border-b" style={{ borderColor: isDark ? '#333333' : '#E5E5E5' }}>
          <span className="text-[9px] font-medium" style={{ color: isDark ? '#888888' : '#666666' }}>
            PAGE SECTIONS
          </span>
        </div>
        <div className="flex-1 p-1.5 space-y-1 overflow-auto">
          {pageSections.map((section, index) => (
            <div
              key={index}
              className="flex items-center gap-1.5 px-2 py-1.5 rounded cursor-pointer transition-all"
              style={{ 
                backgroundColor: section.active 
                  ? (isDark ? '#3B82F620' : '#3B82F610') 
                  : 'transparent',
                border: section.active 
                  ? `1px solid ${isDark ? '#3B82F640' : '#3B82F630'}` 
                  : '1px solid transparent',
                animation: `fadeIn 0.3s ease-out ${animationDelay + 200 + (index * 50)}ms both`
              }}
            >
              <Menu className="w-2.5 h-2.5" style={{ color: isDark ? '#666666' : '#999999' }} />
              <span 
                className="text-[8px] flex-1 truncate"
                style={{ 
                  color: section.active 
                    ? '#3B82F6' 
                    : (isDark ? '#AAAAAA' : '#666666'),
                  fontWeight: section.active ? 500 : 400
                }}
              >
                {section.name}
              </span>
            </div>
          ))}
          <div 
            className="flex items-center justify-center gap-1 px-2 py-1.5 rounded border border-dashed cursor-pointer mt-2"
            style={{ 
              borderColor: isDark ? '#444444' : '#CCCCCC',
              animation: `fadeIn 0.3s ease-out ${animationDelay + 500}ms both`
            }}
          >
            <span className="text-[8px]" style={{ color: isDark ? '#666666' : '#999999' }}>
              + Add Section
            </span>
          </div>
        </div>
      </div>

      {/* Center - Page Preview */}
      <div 
        className="flex-1 p-3 flex items-start justify-center overflow-hidden"
        style={{ backgroundColor: isDark ? '#1A1A1A' : '#F0F0F0' }}
      >
        <div 
          className="w-full max-w-[200px] rounded-lg shadow-lg overflow-hidden"
          style={{ 
            backgroundColor: isDark ? '#0F0F0F' : '#FFFFFF',
            border: `1px solid ${isDark ? '#333333' : '#E0E0E0'}`,
            animation: `scaleIn 0.4s ease-out ${animationDelay + 150}ms both`
          }}
        >
          {/* Mini Nav */}
          <div 
            className="flex items-center justify-between px-3 py-2 border-b"
            style={{ borderColor: isDark ? '#2A2A2A' : '#EEEEEE' }}
          >
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded bg-gradient-to-br from-blue-500 to-purple-500" />
              <div className="h-1.5 w-8 rounded" style={{ backgroundColor: isDark ? '#333333' : '#E5E5E5' }} />
            </div>
            <div className="flex gap-2">
              {[1, 2, 3, 4].map((_, i) => (
                <div 
                  key={i}
                  className="h-1 w-5 rounded" 
                  style={{ backgroundColor: isDark ? '#333333' : '#E5E5E5' }} 
                />
              ))}
            </div>
          </div>

          {/* Hero Section - Active */}
          <div 
            className="p-3 relative"
            style={{ 
              backgroundColor: isDark ? '#1A1A1A' : '#FAFAFA',
              border: `2px solid #3B82F6`,
              margin: '4px',
              borderRadius: '6px',
              animation: `pulse 2s ease-in-out infinite`
            }}
          >
            {/* Selection indicator */}
            <div 
              className="absolute -top-2 left-2 px-1.5 py-0.5 rounded text-[7px] font-medium"
              style={{ backgroundColor: '#3B82F6', color: '#FFFFFF' }}
            >
              Hero Section
            </div>
            
            <div className="space-y-2 mt-1">
              <div 
                className="h-1.5 w-3/4 rounded"
                style={{ 
                  backgroundColor: isDark ? '#3B82F640' : '#3B82F630',
                  animation: `shimmer 2s ease-in-out infinite`
                }}
              />
              <div 
                className="h-3 w-full rounded font-bold text-[8px] flex items-center"
                style={{ color: isDark ? '#FFFFFF' : '#111111' }}
              >
                Turn peak earning years
              </div>
              <div 
                className="h-3 w-4/5 rounded font-bold text-[8px] flex items-center"
                style={{ color: isDark ? '#FFFFFF' : '#111111' }}
              >
                into lifetime wealth.
              </div>
              <div className="h-1 w-full rounded" style={{ backgroundColor: isDark ? '#333333' : '#E5E5E5' }} />
              <div className="h-1 w-5/6 rounded" style={{ backgroundColor: isDark ? '#333333' : '#E5E5E5' }} />
              <div className="flex gap-1.5 mt-2">
                <div 
                  className="h-4 px-2 rounded flex items-center text-[6px] font-medium text-white"
                  style={{ backgroundColor: '#3B82F6' }}
                >
                  Book call
                </div>
                <div 
                  className="h-4 px-2 rounded flex items-center text-[6px]"
                  style={{ 
                    backgroundColor: 'transparent',
                    border: `1px solid ${isDark ? '#444444' : '#CCCCCC'}`,
                    color: isDark ? '#AAAAAA' : '#666666'
                  }}
                >
                  Learn more
                </div>
              </div>
            </div>
          </div>

          {/* Other sections preview */}
          <div className="p-2 space-y-2">
            {[1, 2].map((_, i) => (
              <div 
                key={i}
                className="p-2 rounded"
                style={{ backgroundColor: isDark ? '#1A1A1A' : '#F5F5F5' }}
              >
                <div className="h-1 w-1/3 rounded mb-1" style={{ backgroundColor: isDark ? '#333333' : '#E0E0E0' }} />
                <div className="h-1 w-full rounded" style={{ backgroundColor: isDark ? '#2A2A2A' : '#EBEBEB' }} />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Right Sidebar - Properties */}
      <div 
        className="w-32 border-l flex flex-col"
        style={{ 
          borderColor: isDark ? '#333333' : '#E5E5E5',
          backgroundColor: isDark ? '#222222' : '#FFFFFF',
          animation: `slideInFromRight 0.4s ease-out ${animationDelay + 100}ms both`
        }}
      >
        <div className="p-2 border-b" style={{ borderColor: isDark ? '#333333' : '#E5E5E5' }}>
          <span className="text-[9px] font-medium" style={{ color: isDark ? '#888888' : '#666666' }}>
            PROPERTIES
          </span>
        </div>
        <div className="flex-1 p-2 space-y-3 overflow-auto">
          {/* Element Tools */}
          <div 
            className="grid grid-cols-4 gap-1"
            style={{ animation: `fadeIn 0.3s ease-out ${animationDelay + 300}ms both` }}
          >
            {[MousePointer2, Type, Image, Square].map((Icon, i) => (
              <div 
                key={i}
                className="w-6 h-6 rounded flex items-center justify-center cursor-pointer hover:bg-opacity-80 transition-colors"
                style={{ 
                  backgroundColor: i === 0 ? '#3B82F620' : (isDark ? '#2A2A2A' : '#F0F0F0'),
                  border: i === 0 ? '1px solid #3B82F640' : 'none'
                }}
              >
                <Icon 
                  className="w-3 h-3" 
                  style={{ color: i === 0 ? '#3B82F6' : (isDark ? '#888888' : '#666666') }} 
                />
              </div>
            ))}
          </div>

          {/* AI Suggestion */}
          <div 
            className="p-2 rounded-lg"
            style={{ 
              backgroundColor: isDark ? '#3B82F615' : '#3B82F610',
              border: `1px solid ${isDark ? '#3B82F630' : '#3B82F620'}`,
              animation: `fadeIn 0.4s ease-out ${animationDelay + 400}ms both`
            }}
          >
            <div className="flex items-center gap-1 mb-1">
              <div className="w-3 h-3 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 flex items-center justify-center">
                <span className="text-[6px] text-white">AI</span>
              </div>
              <span className="text-[8px] font-medium" style={{ color: '#3B82F6' }}>
                Suggestion
              </span>
            </div>
            <p className="text-[7px] leading-tight" style={{ color: isDark ? '#AAAAAA' : '#666666' }}>
              Add a trust badge section to increase conversions by 23%
            </p>
            <div 
              className="mt-1.5 flex items-center gap-0.5 text-[7px] cursor-pointer"
              style={{ color: '#3B82F6' }}
            >
              Apply <ChevronRight className="w-2 h-2" />
            </div>
          </div>

          {/* Style Options */}
          <div style={{ animation: `fadeIn 0.3s ease-out ${animationDelay + 500}ms both` }}>
            <span className="text-[8px] font-medium" style={{ color: isDark ? '#666666' : '#999999' }}>
              COLORS
            </span>
            <div className="flex gap-1 mt-1">
              {['#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6'].map((color, i) => (
                <div 
                  key={i}
                  className="w-4 h-4 rounded-full cursor-pointer hover:ring-2 hover:ring-offset-1"
                  style={{ backgroundColor: color }}
                />
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Keyframe animations */}
      <style>{`
        @keyframes slideInFromLeft {
          from { opacity: 0; transform: translateX(-20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes slideInFromRight {
          from { opacity: 0; transform: translateX(20px); }
          to { opacity: 1; transform: translateX(0); }
        }
        @keyframes shimmer {
          0%, 100% { opacity: 0.6; }
          50% { opacity: 1; }
        }
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(59, 130, 246, 0.4); }
          50% { box-shadow: 0 0 0 4px rgba(59, 130, 246, 0.1); }
        }
      `}</style>
    </div>
  );
};

export default FunnelBuilderContent;
